import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart2, 
  Eye, 
  History, 
  ArrowUpDown, 
  RefreshCw,
  Plus,
  Lightbulb,
  TrendingUp,
  Target,
  ArrowRight,
  Clock,
  CheckCircle
} from 'lucide-react';
import { BacktestHistoryWidget } from './BacktestHistoryWidget';
import { StepBasedStrategyDialog } from './StepBasedStrategyDialog';
import { getBacktestsList, SavedBacktest } from '@/services/backtestStorageService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface TestingHubProps {
  onNewStrategy?: (data: any) => void;
}

export const TestingHub: React.FC<TestingHubProps> = ({ onNewStrategy }) => {
  const { toast } = useToast();
  const [backtests, setBacktests] = useState<SavedBacktest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Get the current user's ID
  const getUserId = async () => {
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  };

  // Load backtests from the database
  const loadBacktests = async () => {
    setLoading(true);
    
    try {
      const userId = await getUserId();
      if (!userId) {
        setLoading(false);
        return;
      }

      const backtestsList = await getBacktestsList(userId);
      setBacktests(backtestsList);
    } catch (error) {
      console.error("Error loading backtests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load backtests on component mount
  useEffect(() => {
    loadBacktests();
  }, []);

  // Handle strategy creation
  const handleCreateStrategy = (data: any) => {
    if (onNewStrategy) {
      onNewStrategy(data);
    }
    setShowStrategyDialog(false);
    
    toast({
      title: "Strategy Created",
      description: "Your strategy has been created successfully!",
    });
  };

  // Render workflow steps
  const renderWorkflowSteps = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500 text-white rounded-full">
              <Lightbulb className="h-4 w-4" />
            </div>
            <h3 className="font-medium text-blue-900">1. Ideation</h3>
          </div>
          <p className="text-sm text-blue-700">
            Develop and refine your trading strategy concept
          </p>
        </CardContent>
      </Card>
      
      <Card className="border-2 border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-500 text-white rounded-full">
              <BarChart2 className="h-4 w-4" />
            </div>
            <h3 className="font-medium text-green-900">2. Testing</h3>
          </div>
          <p className="text-sm text-green-700">
            Run backtests to validate your strategy's performance
          </p>
        </CardContent>
      </Card>
      
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500 text-white rounded-full">
              <Target className="h-4 w-4" />
            </div>
            <h3 className="font-medium text-purple-900">3. Deployment</h3>
          </div>
          <p className="text-sm text-purple-700">
            Deploy proven strategies to paper or live trading
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // Render empty state for no backtests
  const renderEmptyState = () => (
    <div className="text-center py-8">
      <div className="max-w-md mx-auto">
        <div className="p-3 bg-blue-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <BarChart2 className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Test Your Ideas?</h3>
        <p className="text-sm text-gray-500 mb-6">
          Create and test your trading strategies with historical data before deploying them live. 
          Our backtesting engine helps you validate your ideas with real market data.
        </p>
        <Button 
          onClick={() => setShowStrategyDialog(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create & Test Strategy
        </Button>
      </div>
    </div>
  );

  // Render backtests summary
  const renderBacktestsSummary = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (backtests.length === 0) {
      return renderEmptyState();
    }

    const recentBacktests = backtests.slice(0, 3);
    const completedBacktests = backtests.filter(b => b.status === 'completed').length;
    const avgReturn = backtests.reduce((sum, b) => sum + (b.performance?.totalReturn || 0), 0) / backtests.length;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart2 className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-600">Total Tests</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{backtests.length}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium text-gray-600">Completed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{completedBacktests}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium text-gray-600">Avg Return</span>
              </div>
              <p className={`text-2xl font-bold ${avgReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {avgReturn >= 0 ? '+' : ''}{avgReturn.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Recent Tests</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setActiveTab('history')}
            >
              View All
            </Button>
          </div>
          
          <div className="space-y-2">
            {recentBacktests.map((backtest) => (
              <Card key={backtest.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm">{backtest.name}</h4>
                    <p className="text-xs text-gray-500">
                      {new Date(backtest.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant={backtest.status === 'completed' ? 'default' : 'secondary'}>
                      {backtest.status}
                    </Badge>
                    <p className={`text-sm font-medium ${
                      (backtest.performance?.totalReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(backtest.performance?.totalReturn || 0) >= 0 ? '+' : ''}
                      {(backtest.performance?.totalReturn || 0).toFixed(2)}%
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="flex justify-center">
          <Button 
            onClick={() => setShowStrategyDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Test New Strategy
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5" />
            Testing Hub
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              {renderWorkflowSteps()}
              {renderBacktestsSummary()}
            </TabsContent>
            
            <TabsContent value="history" className="space-y-4">
              <BacktestHistoryWidget />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Strategy Creation Dialog */}
      <StepBasedStrategyDialog
        open={showStrategyDialog}
        onOpenChange={setShowStrategyDialog}
        onSubmit={handleCreateStrategy}
      />
    </div>
  );
}; 